import React, { useEffect, useState } from 'react'

import {
  PlusIcon,
  MinusIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"

import { 
  ClockIcon, 
  CalendarDaysIcon, 
  UserGroupIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  ShareIcon 
} from "@heroicons/react/24/solid"

import { CircularProgressbarWithChildren } from 'react-circular-progressbar'

import moment from "moment";
import { getTimeSince } from "@/utils/EtGetTimeSince"

import EtModalUpdateUserAction from '@/components/common/modal/EtModalUpdateUserAction'

const EtActionCard = ({
    action, 
    actionIndex, 
    refetchUserActions,
    showEdit=true,
    showDelete=false,
}) => {
  // Author - Atul Pandey
  // First Created - 12 Jun 2025
  // Purpose - To display each action details
  // Inherited from ActionCard component inside EtTrinityWebLandingAction
  // Updated - 21 Jun 2025 - added showEdit and showDelete props for controlling edit and delete options
  // Updated - 21 Jun 2025 - fix-nested-lists class for editor content
  
  
  // Original Update Trail
  // ----------------------
  // First Created - 03 Apr 2025
  // Purpose - To display each action detail
  // Updated - 04 Apr 2025 - added edit features for an action
  // Updated - 04 Apr 2025 - added refetchActions prop from the parent

  // 0 - Validate incoming data
  // console.log('action in ActionCard', action)

  // 1 - State Variables
  const [showNoteDetails, setShowNoteDetails] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false) // 04 Apr 2025
  const [selectedAction, setSelectedAction] = useState(null)    // 04 Apr 2025


  // 2 - Extract action data
  const actionName = action?.userActionName || ""
  const actionDesc = action?.userActionDesc || ""
  const actionProgress = action?.userActionProgress || 0
  const completeBy = action?.completeBy || ""
  const lastUpdated = action?.updatedAt || ""
  const actionNotes = action?.userActionNotes || []

  // 3 - 🧠 Hhelper function for Tracking Status
  const getTrackingStatus = () => {
    const daysToGo = moment(completeBy).startOf('day').diff(moment().startOf('day'), 'days');
    let status = 'Ok';
  let bgColor = 'bg-lime-600';     // ✅ Dot color (default green)
  let textColor = 'text-lime-600'; // ✅ Text color (default green)

  if (actionProgress === 0) {
    if (daysToGo > 5) {
      status = 'Ok';
      bgColor = 'bg-lime-600'; textColor = 'text-lime-600';
    } else if (daysToGo > 3) {
      status = 'Watch';
      bgColor = 'bg-orange-400'; textColor = 'text-orange-800';
    } else if (daysToGo >= 2) {
      status = 'At Risk';
      bgColor = 'bg-orange-600'; textColor = 'text-red-600';
    } else if (daysToGo >= 1) {
      status = 'High Risk';
      bgColor = 'bg-red-400'; textColor = 'text-red-600';
    } else {
      status = 'Alert!';
      bgColor = 'bg-red-600'; textColor = 'text-red-600';
    }
  } else if (actionProgress < 75) {
    if (daysToGo > 5) {
      status = 'Ok';
      bgColor = 'bg-lime-600'; textColor = 'text-lime-600';
    } else if (daysToGo >= 2) {
      status = 'Watch';
      bgColor = 'bg-orange-200'; textColor = 'text-orange-800';
    } else if (daysToGo >= 1) {
      status = 'High Risk';
      bgColor = 'bg-red-400'; textColor = 'text-red-600';
    } else {
      status = 'Alert!';
      bgColor = 'bg-red-600'; textColor = 'text-red-600';
    }
  } else if (actionProgress < 90) {
    if (daysToGo > 5) {
      status = 'On Track';
      bgColor = 'bg-lime-600'; textColor = 'text-lime-600';
    } else if (daysToGo >= 2) {
      status = 'Watch';
      bgColor = 'bg-orange-200'; textColor = 'text-orange-800';
    } else if (daysToGo >= 1) {
      status = 'High Risk';
      bgColor = 'bg-red-400'; textColor = 'text-red-600';
    } else {
      status = 'Alert!';
      bgColor = 'bg-red-600'; textColor = 'text-red-600';
    }
  } else if (actionProgress < 95) {
    if (daysToGo > 5) {
      status = 'On Track';
      bgColor = 'bg-lime-600'; textColor = 'text-lime-600';
    } else if (daysToGo >= 1) {
      status = 'Watch';
      bgColor = 'bg-orange-200'; textColor = 'text-orange-800';
    } else {
      status = 'Alert!';
      bgColor = 'bg-red-600'; textColor = 'text-red-600';
    }
  } else if (actionProgress < 100) {
    if (daysToGo > 5) {
      status = 'On Track';
      bgColor = 'bg-lime-600'; textColor = 'text-lime-600';
    } else if (daysToGo >= 1) {
      status = 'Watch';
      bgColor = 'bg-orange-200'; textColor = 'text-orange-800';
    } else {
      status = 'Alert!';
      bgColor = 'bg-red-600'; textColor = 'text-red-600';
    }
  }
  
    return { status, bgColor, textColor };
  };

  const { status, bgColor, textColor } = getTrackingStatus();

  // 4 - Create Markup
  const createMarkup = (htmlContent) => {
    return { __html: htmlContent }
  }

  return (
    <div className="bg-white rounded m-1 p-2 shadow">
      {/* Header */}
      <div>
        <div className='flex justify-between items-center'>
          <div className='flex items-center justify-start'>
            <div
              onClick={() => setShowNoteDetails(prev => !prev)}
              className="rounded-full h-5 w-5 hover:bg-white flex justify-center items-center cursor-pointer"
            >
              {showNoteDetails ? (
                <MinusIcon className="h-4 w-4 text-orange-600" />
              ) : (
                <PlusIcon className="h-4 w-4 text-orange-600" />
              )}
            </div>
            <p className='text-lg font-bold text-gray-600 ml-2'>{actionIndex + 1}.</p>
            <p className='text-xs text-gray-600 ml-2'>{actionName}</p>
          </div>
          {/* 21 Jun 2025 - added ShowEdit state */}
          {showEdit && ( 
          <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
            <PencilSquareIcon
              className="h-4 w-4 p-[2px] text-orange-600 cursor-pointer"
              onClick={() => {
                setSelectedAction(action)
                setShowUpdateModal(true)
              }}
            />
          </div>)}
        </div>

        <div className='grid grid-cols-4 gap-4 justify-between items-center border-t pt-2'>
            {/* Progress */}
            <div className='col-span-1 flex justify-center items-center'>
              <div className='h-10 w-10 z-0 relative'>
                <CircularProgressbarWithChildren
                  value={actionProgress}
                  circleRatio={0.75}
                  styles={{
                    path: { stroke: '#552a9a', transform: 'rotate(-135deg)', transformOrigin: 'center center' },
                    trail: { stroke: '#d6d6d6', transform: 'rotate(-135deg)', transformOrigin: 'center center' }
                  }}
                >
                  <div><p className='text-xs font-bold text-orange-600'>{actionProgress}%</p></div>
                </CircularProgressbarWithChildren>
              </div>
            </div>

            {/* Last Updated */}
            <div className='col-span-1'>
              <p className="text-[10px] px-2 rounded-full bg-lime-200 inline-block">{getTimeSince(lastUpdated)}</p>
              <p className="text-[9px] italic text-gray-400">last updated</p>
            </div>

            {/* Complete By */}
            <div className='col-span-1 flex flex-col justify-center items-center'>
              <div className='flex items-center justify-center'>
                <CalendarDaysIcon className="h-4 w-4 text-orange-600 mr-1" />
                <p className='text-[10px]'>Complete by:</p>
              </div>
              {/* <p className='text-[9px] bg-white border border-orange-400 rounded-full px-2 inline-block'>
                {moment(completeBy).format("DD MMM YYYY")}
              </p> */}
              <p
                  className={`text-[9px] rounded-full px-2 inline-block border 
                    ${moment(completeBy).isBefore(moment(), 'day')
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-gray-600 border-orange-400'}`}
                >
                  {moment(completeBy).format("DD MMM YYYY")}
              </p>
            </div>

            {/* Tracking Status */}
            <div className='col-span-1 flex flex-col justify-end items-center'>
              <div className={`w-6 h-6 rounded-full border ${bgColor}`}>
              </div>
              <p className={`text-[9px] px-2 py-[2px] ${textColor}`}>
                {status}
              </p>
              
            </div>
        </div>

      </div>

      {/* Expanded Details */}
      {showNoteDetails && (
        <div className='mt-2 space-y-2'>
          {actionDesc && (
            <div className='bg-gray-100 rounded p-2 text-xs fix-nested-lists' dangerouslySetInnerHTML={createMarkup(actionDesc)} />
          )}
          
          {/* Trail Placeholder */}
          {actionNotes?.length > 0 && (
            <div className="border-t mt-2 pt-2">
              <p className="text-xs font-bold text-gray-600">Progress Trail</p>
              {actionNotes
                ?.slice()
                ?.sort((a, b) => new Date(b.actionNoteDate) - new Date(a.actionNoteDate))
                ?.map((note, noteIndex) => {
                  const noteText = note?.actionNoteText || ""
                  const noteDate = note?.actionNoteDate || ""
                  const noteProgress = note?.actionNoteProgress || 0

                  return (
                    <div key={note._id + noteIndex} className='bg-gray-50 rounded my-1 p-2'>
                      <div className='flex items-center'>
                        <div className='h-8 w-8 mr-2'>
                          <CircularProgressbarWithChildren
                            value={noteProgress}
                            circleRatio={0.75}
                            styles={{
                              path: { stroke: '#552a9a', transform: 'rotate(-135deg)', transformOrigin: 'center center' },
                              trail: { stroke: '#d6d6d6', transform: 'rotate(-135deg)', transformOrigin: 'center center' }
                            }}
                          >
                            <div><p className='text-[10px] font-bold'>{noteProgress}%</p></div>
                          </CircularProgressbarWithChildren>
                        </div>
                        <div className="flex-1">
                          <p className='text-[11px]'>{noteText}</p>
                          <p className='text-[9px] italic text-gray-400'>{getTimeSince(noteDate)}</p>
                        </div>
                      </div>
                    </div>
                  )
                })}
            </div>
          )}

          {actionNotes?.length === 0 && (
            <p className='text-xs italic text-gray-500'>No progress updates yet.</p>
          )}
        </div>
      )}
      {/* Modal to update the action - 04 Apr 2025 */}
      {showUpdateModal && selectedAction && (
        <EtModalUpdateUserAction
          isOpen={showUpdateModal}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedAction(null);
          }}
          actionId={selectedAction._id}
          actionData={selectedAction}
          refetchUserActions={refetchUserActions}
        />
      )}
    </div>
  )

}

export default EtActionCard